import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchBookById, updateBook, fetchCategories } from '../api';
import PdfDropZone from '../components/PdfDropZone';

const AdminUpdatePage = ({ user }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const [formData, setFormData] = useState({
    book_url_id: '',
    title: '',
    author: '',
    description: '',
    price: '',
    orig: '',
    stock: '',
    pages: '',
    year: new Date().getFullYear(),
    isbn: '',
    publisher: '',
    genre: '',
    cat: '',
    isFree: false,
  });

  const [coverImage, setCoverImage] = useState(null);
  const [bookPreviewPdf, setBookPreviewPdf] = useState(null);
  const [currentImageUrl, setCurrentImageUrl] = useState("");
  const [currentPdfUrl, setCurrentPdfUrl] = useState("");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const loadBook = async () => {
      try {
        const book = await fetchBookById(id);
        const newFormData = { ...formData };
        Object.keys(newFormData).forEach(key => {
          if (book[key] !== undefined) {
             newFormData[key] = book[key];
          }
        });
        setFormData(newFormData);
        setCurrentImageUrl(book.coverImage || book.img || "");
        setCurrentPdfUrl(book.pdfUrl || "");
      } catch (err) {
        alert("Failed to fetch book data.");
        navigate('/admin');
      } finally {
        setFetching(false);
      }
    };
    const loadCategories = async () => {
      try {
        const data = await fetchCategories();
        setCategories(data.filter(c => c.id !== 'all'));
      } catch (err) {
        console.error("Failed to load categories:", err);
      }
    };
    loadBook();
    loadCategories();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      const uploadData = new FormData();
      Object.keys(formData).forEach(key => {
          uploadData.append(key, formData[key]);
      });
      if (coverImage) {
        uploadData.append('coverImage', coverImage);
      }
      if (bookPreviewPdf) {
        uploadData.append('bookPreviewPdf', bookPreviewPdf);
      }

      await updateBook(user.token, id, uploadData);

      alert('Book updated successfully!');
      navigate('/admin');
    } catch (error) {
      console.error(error);
      alert(error.message || 'Failed to update book');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid var(--border)',
    background: 'white',
    fontFamily: 'inherit',
    fontSize: '0.95rem'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '0.9rem',
    fontWeight: '600',
    marginBottom: '6px',
    color: 'var(--text2)'
  };

  if (fetching) {
     return <div style={{ textAlign: "center", padding: "80px 20px" }}>Loading Book Data...</div>;
  }

  return (
    <div className="admin-page-container" style={{ minHeight: "100vh", backgroundColor: "var(--cream)", padding: "80px 20px" }}>
      <style>{`
        .admin-form-card {
            max-width: 800px;
            margin: 0 auto;
            backgroundColor: var(--paper);
            padding: 40px;
            borderRadius: var(--r);
            boxShadow: var(--shadow-lg);
        }
        .form-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
        .form-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 24px; }
        .form-grid-price { display: grid; grid-template-columns: 1fr 1fr auto; gap: 24px; align-items: flex-end; }
        
        @media (max-width: 768px) {
            .admin-page-container { padding: 40px 16px !important; }
            .admin-form-card { padding: 24px !important; }
            .form-grid-2, .form-grid-3, .form-grid-price { 
                grid-template-columns: 1fr !important; 
                gap: 16px !important;
            }
            .checkbox-wrapper { padding-bottom: 0 !important; margin-top: 8px; }
            .preview-img { height: 100px !important; }
        }
      `}</style>
      <div className="admin-form-card" style={{ background: 'var(--paper)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '40px' }}>
          <div style={{
            height: '60px', width: '60px', backgroundColor: 'var(--cream2)',
            borderRadius: '12px', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: '24px'
          }}>
            ✏️
          </div>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--text)', margin: '0 0 5px 0' }}>Update Book</h1>
            <p style={{ color: 'var(--text-light)', margin: 0 }}>Edit the details of '{formData.title}'</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

          <div>
            <label style={labelStyle}>Book URL ID (Optional)</label>
            <input
              style={inputStyle}
              type="text"
              value={formData.book_url_id}
              onChange={e => setFormData({ ...formData, book_url_id: e.target.value })}
              placeholder="e.g. 69ba9e302a671ed6bc0b06dc"
            />
          </div>

          <div className="form-grid-2">
            <div>
              <label style={labelStyle}>Book Title *</label>
              <input style={inputStyle} value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} placeholder="Enter book title" required />
            </div>
            <div>
              <label style={labelStyle}>Author *</label>
              <input style={inputStyle} value={formData.author} onChange={e => setFormData({ ...formData, author: e.target.value })} placeholder="Author name" required />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Description</label>
            <textarea
              style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              placeholder="Book description"
            />
          </div>

          <div className="form-grid-price">
            <div>
              <label style={labelStyle}>Price (₹) *</label>
              <input style={inputStyle} type="number" min="0" step="0.01" value={formData.price} onChange={e => setFormData({ ...formData, price: e.target.value })} placeholder="0" required />
            </div>
            <div>
              <label style={labelStyle}>Original Price (₹)</label>
              <input style={inputStyle} type="number" min="0" step="0.01" value={formData.orig} onChange={e => setFormData({ ...formData, orig: e.target.value })} placeholder="0" />
            </div>
            <div className="checkbox-wrapper" style={{ paddingBottom: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input type="checkbox" checked={formData.isFree} onChange={e => setFormData({ ...formData, isFree: e.target.checked })} style={{ width: '18px', height: '18px' }} />
                <span style={{ fontSize: '0.95rem', fontWeight: '600' }}>Mark as Free</span>
              </label>
            </div>
          </div>

          <div className="form-grid-3">
            <div>
              <label style={labelStyle}>Stock</label>
              <input style={inputStyle} type="number" min="0" value={formData.stock} onChange={e => setFormData({ ...formData, stock: e.target.value })} placeholder="0" />
            </div>
            <div>
              <label style={labelStyle}>Pages</label>
              <input style={inputStyle} type="number" min="0" value={formData.pages} onChange={e => setFormData({ ...formData, pages: e.target.value })} placeholder="0" />
            </div>
            <div>
              <label style={labelStyle}>Year</label>
              <input style={inputStyle} type="number" value={formData.year} onChange={e => setFormData({ ...formData, year: e.target.value })} placeholder={new Date().getFullYear()} />
            </div>
          </div>

          <div className="form-grid-2">
            <div>
              <label style={labelStyle}>Category (cat)</label>
              <input style={inputStyle} list="category-options-update" value={formData.cat} onChange={e => setFormData({ ...formData, cat: e.target.value })} placeholder="Select..." />
              <datalist id="category-options-update">
                {categories.map(c => <option key={c.id} value={c.label} />)}
              </datalist>
            </div>
            <div>
              <label style={labelStyle}>Genre</label>
              <input style={inputStyle} value={formData.genre} onChange={e => setFormData({ ...formData, genre: e.target.value })} placeholder="e.g. self-help" />
            </div>
          </div>

          <div className="form-grid-2">
            <div>
              <label style={labelStyle}>Publisher</label>
              <input style={inputStyle} value={formData.publisher} onChange={e => setFormData({ ...formData, publisher: e.target.value })} placeholder="Publisher" />
            </div>
            <div>
              <label style={labelStyle}>ISBN</label>
              <input style={inputStyle} value={formData.isbn} onChange={e => setFormData({ ...formData, isbn: e.target.value })} placeholder="ISBN" />
            </div>
          </div>

          <div style={{ paddingTop: '24px', borderTop: '1px solid var(--border)' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Cover Image</h3>
            
            {currentImageUrl && currentImageUrl.includes('public.blob.vercel-storage.com') && !coverImage && (
              <div style={{
                background: '#fff3f3', border: '1px solid #f5c6cb', borderRadius: '8px',
                padding: '12px 16px', marginBottom: '15px', color: '#721c24',
                fontSize: '0.9rem', fontWeight: 600,
              }}>
                ⚠️ The current cover image was stored on Vercel Blob and is no longer accessible. Please upload a new image below.
              </div>
            )}

            {currentImageUrl && !currentImageUrl.includes('public.blob.vercel-storage.com') && !coverImage && (
              <div style={{ marginBottom: "15px" }}>
                <p style={{ fontSize: "0.85rem", color: "var(--text-light)", marginBottom: "8px" }}>Current Image Preview:</p>
                <img className="preview-img" src={currentImageUrl} alt="Current cover" style={{ height: "120px", borderRadius: "8px", border: "1px solid var(--border)", objectFit: 'contain' }} />
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Upload New Image File (Replaces Current)</label>
              <input type="file" accept="image/*" onChange={e => setCoverImage(e.target.files[0])} style={{ ...inputStyle, padding: '12px' }} />
            </div>
          </div>

          <div style={{ paddingTop: '24px', borderTop: '1px solid var(--border)' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Book Preview PDF</h3>
            
            {currentPdfUrl && currentPdfUrl.includes('public.blob.vercel-storage.com') && !bookPreviewPdf && (
              <div style={{
                background: '#fff3f3', border: '1px solid #f5c6cb', borderRadius: '8px',
                padding: '12px 16px', marginBottom: '15px', color: '#721c24',
                fontSize: '0.9rem', fontWeight: 600,
              }}>
                ⚠️ The current preview PDF was stored on Vercel Blob and is no longer accessible. Please upload a new PDF below.
              </div>
            )}

            <PdfDropZone
              label="Upload New Preview PDF"
              selectedFile={bookPreviewPdf}
              onFileSelect={setBookPreviewPdf}
              currentFileUrl={currentPdfUrl && !currentPdfUrl.includes('public.blob.vercel-storage.com') ? currentPdfUrl : ""}
            />
          </div>

          <div style={{ display: 'flex', gap: '16px', marginTop: '10px' }}>
            <button type="button" onClick={() => navigate('/admin')} className="btn btn-ghost" style={{ flex: 1, padding: '14px', fontSize: '16px', justifyContent: 'center' }}>Cancel</button>
            <button type="submit" className="btn btn-forest" disabled={loading} style={{ flex: 2, padding: '14px', fontSize: '16px', justifyContent: 'center' }}>
              {loading ? 'Updating...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminUpdatePage;
